/** Portability: a client asking to take over gateways of another client. */
export type TransferStatus = 'pending' | 'accepted' | 'refused' | 'cancelled';
export const TRANSFER_STATUSES: TransferStatus[] = ['pending', 'accepted', 'refused', 'cancelled'];

export type TransferBox = 'inbox' | 'sent' | 'all';

export type TransferProblem = 'unknown_devices' | 'already_mine' | 'several_owners' | 'several_types' | 'no_admin' | 'no_template';

/** A client as the API names it. Both fields are blank when the environment hides the current owner from the requester. */
export interface ClientRef {
  id: string;
  name: string | null;
}

/** One current owner among the gateways typed: how many it holds, how many of its administrators will be asked. */
export interface CedingClient extends ClientRef {
  deviceCount: number;
  adminCount: number;
}

export interface PersonRef {
  id: string;
  username: string;
  email?: string | null;
  firstName?: string | null;
  lastName?: string | null;
}

export interface TemplateRef {
  id: number;
  name: string;
  isDefault: boolean;
  isAnep: boolean;
  /** A template of the taking client's head office. */
  isRoot: boolean;
}

export interface ResolvedDevice {
  idDevice: string;
  found: boolean;
  clientId?: string | null;
  clientName?: string | null;
  deviceType?: string | null;
  alreadyMine: boolean;
}

/** What the requester sees before sending: how many administrators will be asked, never who. */
export interface TransferResolved {
  toClient: ClientRef;
  devices: ResolvedDevice[];
  /** The one current owner; null when there are several. */
  fromClient: ClientRef | null;
  /** Every current owner; several only when the environment allows agencies of one group together. */
  fromClients: CedingClient[];
  deviceType: string | null;
  adminCount: number;
  templates: TemplateRef[];
  problems: TransferProblem[];
}

export interface DeviceTransfer {
  id: number;
  status: TransferStatus;
  fromClient: ClientRef;
  toClient: ClientRef;
  deviceType: string;
  deviceIds: string[];
  deviceCount: number;
  templateId: number;
  templateName: string | null;
  /** How many administrators of the current client were emailed; their names stay with them. */
  notifiedCount: number;
  requestedBy: PersonRef;
  message: string | null;
  decidedBy: string | null;
  decidedAt: string | null;
  decisionComment: string | null;
  result: Array<{ idDevice: string; moved: boolean; detail: string | null }> | null;
  createdAt: string | null;
  updatedAt: string | null;
  canDecide: boolean;
  canCancel: boolean;
}

export interface DeviceTransferList {
  items: DeviceTransfer[];
  total: number;
  page: number;
  limit: number;
  pages: number;
}

/** What one submission opened: one request per current owner. */
export interface TransferCreated {
  items: DeviceTransfer[];
  total: number;
}

export interface TransferCreate {
  device_ids: string[];
  to_client_id?: string;
  template_id: number;
  message: string;
}
